'use client';

import { useState } from 'react';
import ServiceCard from './ServiceCard';
import CategoryFilter from './CategoryFilter';
import BeforeAfterGallery from './BeforeAfterGallery';
import PricingCalculator from './PricingCalculator';
import ServiceFAQ from './ServiceFAQ';
import QuickQuoteWidget from './QuickQuoteWidget';
import Icon from '@/components/ui/AppIcon';


interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  priceRange: string;
  features: string[];
  category: string;
}

interface GalleryImage {
  before: string;
  beforeAlt: string;
  after: string;
  afterAlt: string;
  title: string;
  description: string;
}

interface PricingOption {
  id: string;
  label: string;
  basePrice: number;
}

interface FAQItem {
  question: string;
  answer: string;
}

export default function ServicesInteractive() {
  const [activeCategory, setActiveCategory] = useState('Tous');

  const categories = [
  'Tous',
  'Nettoyage',
  'Résidentiel',
  'Conciergerie',
  'Spécialisé',
  'Multi-services'];


  const services: Service[] = [
  {
    id: '1',
    title: 'Nettoyage Résidentiel Complet',
    description: 'Service de nettoyage professionnel pour votre maison. Nous prenons soin de chaque détail pour vous offrir un environnement propre et sain.',
    icon: 'HomeIcon',
    image: "https://images.unsplash.com/photo-1718476866101-a45c224e28f3",
    imageAlt: 'Modern living room with white sofa and clean wooden floors being professionally cleaned',
    priceRange: 'À partir de 45 CHF/h',
    features: [
    'Nettoyage de toutes les pièces',
    'Dépoussiérage complet des surfaces',
    'Nettoyage des sols et aspirateur',
    'Nettoyage des sanitaires',
    'Nettoyage des vitres intérieures',
    'Produits écologiques inclus'],

    category: 'Résidentiel'
  },
  {
    id: '2',
    title: 'Nettoyage de Bureaux',
    description: 'Maintenez un environnement de travail professionnel et hygiénique. Service adapté aux horaires de votre entreprise.',
    icon: 'BuildingOfficeIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_135905e53-1767540336507.png",
    imageAlt: 'Modern office space with glass walls and clean desks in bright natural lighting',
    priceRange: 'À partir de 40 CHF/h',
    features: [
    'Nettoyage des espaces de travail',
    'Désinfection des surfaces communes',
    'Vidage des poubelles',
    'Nettoyage des sanitaires professionnels',
    'Entretien des sols',
    'Service hors heures disponible'],

    category: 'Nettoyage'
  },
  {
    id: '3',
    title: 'Nettoyage Après Construction',
    description: 'Élimination complète des débris et poussières après travaux. Votre espace prêt à être utilisé.',
    icon: 'WrenchScrewdriverIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d262d09-1766734425454.png",
    imageAlt: 'Construction site interior with dust and debris being cleaned by professional team',
    priceRange: 'À partir de 55 CHF/h',
    features: [
    'Élimination des débris de construction',
    'Nettoyage en profondeur des surfaces',
    'Nettoyage des vitres post-travaux',
    'Dépoussiérage intensif',
    'Nettoyage des sols spécialisé',
    'Évacuation des déchets incluse'],

    category: 'Spécialisé'
  },
  {
    id: '4',
    title: 'Services de Conciergerie',
    description: 'Gestion complète de l\'entretien de votre immeuble. Service régulier et fiable pour les copropriétés.',
    icon: 'BuildingOffice2Icon',
    image: "https://images.unsplash.com/photo-1719483534786-4d1d7bd79fc8",
    imageAlt: 'Elegant apartment building entrance hall with marble floors and modern lighting',
    priceRange: 'Sur devis',
    features: [
    'Nettoyage des parties communes',
    'Entretien des escaliers',
    'Nettoyage des ascenseurs',
    'Gestion des poubelles',
    'Entretien des espaces verts',
    'Service régulier programmé'],

    category: 'Conciergerie'
  },
  {
    id: '5',
    title: 'Nettoyage de Vitres',
    description: 'Service professionnel de nettoyage de vitres intérieures et extérieures. Résultat impeccable garanti.',
    icon: 'WindowIcon',
    image: "https://images.unsplash.com/photo-1733271751327-140114dbdb4c",
    imageAlt: 'Professional window cleaner working on large glass windows of modern building',
    priceRange: 'À partir de 35 CHF/h',
    features: [
    'Nettoyage vitres intérieures',
    'Nettoyage vitres extérieures',
    'Nettoyage des cadres',
    'Produits anti-traces',
    'Équipement professionnel',
    'Résultat sans traces garanti'],

    category: 'Nettoyage'
  },
  {
    id: '6',
    title: 'Nettoyage de Tapis et Moquettes',
    description: 'Nettoyage en profondeur de vos tapis et moquettes. Élimination des taches et des odeurs.',
    icon: 'SparklesIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_104be000d-1766774405521.png",
    imageAlt: 'Beige carpet being deep cleaned with professional steam cleaning equipment',
    priceRange: 'À partir de 50 CHF/h',
    features: [
    'Nettoyage vapeur professionnel',
    'Élimination des taches tenaces',
    'Désodorisation complète',
    'Séchage rapide',
    'Produits hypoallergéniques',
    'Protection anti-taches optionnelle'],

    category: 'Spécialisé'
  },
  {
    id: '7',
    title: 'Jardinage et Entretien Extérieur',
    description: 'Entretien complet de vos espaces verts. Tonte, taille, et embellissement de votre jardin.',
    icon: 'HomeModernIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_111ed0ee3-1768212311544.png",
    imageAlt: 'Well-maintained garden with trimmed hedges and freshly mowed green lawn',
    priceRange: 'À partir de 40 CHF/h',
    features: [
    'Tonte de pelouse',
    'Taille de haies et arbustes',
    'Désherbage',
    'Ramassage des feuilles',
    'Entretien saisonnier',
    'Conseil en jardinage'],

    category: 'Multi-services'
  },
  {
    id: '8',
    title: 'Petits Travaux de Bricolage',
    description: 'Assistance pour vos petits travaux du quotidien. Montage de meubles, fixations, et réparations mineures.',
    icon: 'WrenchIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c32b056-1767292545909.png",
    imageAlt: 'Handyman assembling furniture with professional tools in modern home interior',
    priceRange: 'À partir de 45 CHF/h',
    features: [
    'Montage de meubles',
    'Installation d\'étagères',
    'Fixations murales',
    'Petites réparations',
    'Remplacement d\'ampoules',
    'Conseil technique'],

    category: 'Multi-services'
  }];


  const galleryImages: GalleryImage[] = [
  {
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_169e12d60-1767111456179.png",
    beforeAlt: 'Messy living room with scattered items and dusty surfaces before cleaning',
    after: "/assets/images/living_room_after_organized_clean_v2.png",
    afterAlt: 'Modern clean organized living room with neatly arranged furniture after professional cleaning service',
    title: 'Nettoyage Résidentiel Complet',
    description: 'Transformation d\'un salon après notre service de nettoyage en profondeur'
  },
  {
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_12ef5d1a2-1767016179222.png",
    beforeAlt: 'Cluttered kitchen with dirty countertops and unwashed dishes',
    after: "https://images.unsplash.com/photo-1722649934574-49bbddd3b5ae",
    afterAlt: 'Pristine kitchen with sparkling countertops and organized cabinets',
    title: 'Nettoyage de Cuisine',
    description: 'Cuisine entièrement nettoyée et désinfectée par nos professionnels'
  },
  {
    before: "https://img.rocket.new/generatedImages/rocket_gen_img_1312e9488-1767673626294.png",
    beforeAlt: 'Dusty office space with cluttered desks and dirty windows',
    after: "https://img.rocket.new/generatedImages/rocket_gen_img_19d685136-1764681426609.png",
    afterAlt: 'Clean modern office with organized desks and crystal clear windows',
    title: 'Nettoyage de Bureau',
    description: 'Espace de travail professionnel après notre intervention'
  },
  {
    before: "/assets/images/7cdc2f9d-a6f4-47c2-9ef2-d243a92dc829-1768744442687.jpeg",
    beforeAlt: 'Bathroom with soap residue and water stains before deep cleaning',
    after: "/assets/images/IMG_0017-1768749323396.png",
    afterAlt: 'Sparkling clean bathroom with pristine fixtures and gleaming surfaces',
    title: 'Nettoyage de Salle de Bain',
    description: 'Salle de bain entièrement désinfectée et nettoyée en profondeur'
  },
  {
    before: "/assets/images/7cdc2f9d-a6f4-47c2-9ef2-d243a92dc829-1768744587640.jpeg",
    beforeAlt: 'Dirty floor with stains and debris before professional cleaning',
    after: "/assets/images/IMG_8276-1768746147743.jpeg",
    afterAlt: 'Gleaming clean floor with restored shine after professional treatment',
    title: 'Nettoyage de Sols',
    description: 'Sol professionnel nettoyé et restauré avec nos techniques spécialisées'
  },
  {
    before: "/assets/images/7cdc2f9d-a6f4-47c2-9ef2-d243a92dc829-1768744903570.jpeg",
    beforeAlt: 'Carpet with visible stains and dirt before deep cleaning',
    after: "/assets/images/11958198-BEF2-462C-BE09-83DB57C854BC-1768765478440.png",
    afterAlt: 'Fresh and clean carpet after professional steam cleaning treatment',
    title: 'Nettoyage de Tapis',
    description: 'Tapis profondément nettoyé avec élimination complète des taches'
  },
  {
    before: "/assets/images/window_cleaning_before_dirty_streaky.png",
    beforeAlt: 'Dirty window with streaks, water spots, and grime buildup blocking natural light',
    after: "/assets/images/window_cleaning_after_crystal_clear.png",
    afterAlt: 'Crystal clear sparkling clean window with streak-free glass and bright natural light',
    title: 'Nettoyage de Vitres',
    description: 'Vitres nettoyées professionnellement sans traces pour une clarté parfaite'
  },
  {
    before: "/assets/images/office_carpet_before_heavily_soiled.png",
    beforeAlt: 'Office carpet heavily soiled with dark stains and discolored high-traffic areas',
    after: "/assets/images/office_carpet_after_professionally_cleaned.png",
    afterAlt: 'Office carpet professionally cleaned with vibrant color restored and stains removed',
    title: 'Nettoyage de Moquette de Bureau',
    description: 'Moquette de bureau restaurée avec élimination des taches tenaces'
  },
  {
    before: "/assets/images/construction_site_before_dusty_debris.png",
    beforeAlt: 'Dusty construction site with concrete dust covering floors and debris scattered everywhere',
    after: "/assets/images/construction_site_after_spotless_clean.png",
    afterAlt: 'Spotless construction site after post-construction cleaning, dust-free and ready for occupancy',
    title: 'Nettoyage Après Construction',
    description: 'Site de construction entièrement nettoyé et prêt pour l\'occupation'
  }];



  const pricingOptions: PricingOption[] = [
  { id: 'basic', label: 'Nettoyage de base', basePrice: 45 },
  { id: 'deep', label: 'Nettoyage en profondeur', basePrice: 65 },
  { id: 'windows', label: 'Nettoyage des vitres', basePrice: 35 },
  { id: 'kitchen', label: 'Nettoyage cuisine approfondi', basePrice: 55 },
  { id: 'bathroom', label: 'Nettoyage sanitaires', basePrice: 40 }];


  const faqs: FAQItem[] = [
  {
    question: 'Quels produits utilisez-vous pour le nettoyage?',
    answer: 'Nous utilisons exclusivement des produits écologiques et hypoallergéniques, certifiés pour leur efficacité et leur respect de l\'environnement. Tous nos produits sont conformes aux normes suisses de qualité et de sécurité.'
  },
  {
    question: 'Proposez-vous des services réguliers?',
    answer: 'Oui, nous proposons des contrats de nettoyage régulier avec des tarifs préférentiels. Vous pouvez choisir la fréquence qui vous convient: hebdomadaire, bimensuelle ou mensuelle. Des réductions sont appliquées pour les services récurrents.'
  },
  {
    question: 'Êtes-vous assurés?',
    answer: 'Absolument. Nous disposons d\'une assurance responsabilité civile complète couvrant tous nos services. Votre tranquillité d\'esprit est notre priorité.'
  },
  {
    question: 'Combien de temps à l\'avance dois-je réserver?',
    answer: 'Nous recommandons de réserver au moins 48 heures à l\'avance pour garantir la disponibilité. Cependant, nous faisons de notre mieux pour accommoder les demandes urgentes selon nos disponibilités.'
  },
  {
    question: 'Dois-je fournir le matériel de nettoyage?',
    answer: 'Non, nous apportons tout le matériel et les produits nécessaires. Si vous préférez que nous utilisions vos propres produits, nous pouvons également nous adapter à votre demande.'
  },
  {
    question: 'Intervenez-vous en dehors des heures de bureau?',
    answer: 'Oui, nous proposons des services flexibles adaptés à vos horaires. Pour les bureaux et commerces, nous pouvons intervenir en soirée ou le week-end pour ne pas perturber votre activité.'
  }];


  const filteredServices =
  activeCategory === 'Tous' ?
  services :
  services.filter((service) => service.category === activeCategory);

  return (
    <div className="space-y-12">
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
          Filtrer par catégorie
        </h2>
        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory} />

      </div>

      <div className="space-y-8">
        {filteredServices.map((service) =>
        <div key={service.id} className="bg-card rounded-xl shadow-lg border border-border overflow-hidden hover:shadow-xl transition-shadow duration-300">
          <ServiceCard {...service} />
        </div>
        )}
      </div>

      {filteredServices.length === 0 &&
      <div className="bg-card rounded-lg shadow-md p-12 text-center border border-border">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="MagnifyingGlassIcon" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-xl font-heading font-bold text-foreground mb-2">
            Aucun service trouvé
          </h3>
          <p className="text-muted-foreground">
            Essayez de sélectionner une autre catégorie
          </p>
        </div>
      }

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BeforeAfterGallery images={galleryImages} />
        <PricingCalculator
          serviceType="Nettoyage Résidentiel"
          options={pricingOptions} />

      </div>

      <ServiceFAQ faqs={faqs} />

      <QuickQuoteWidget />
    </div>);

}